import React, { useEffect, useState } from "react";
import {
    loadCaptchaEnginge,
    validateCaptcha,
    LoadCanvasTemplateNoReload,
} from "react-simple-captcha";
import Header from "./Header";

interface Certificate {
    number: string;
    title: string;
    duration: string;
    days: string;
    status: string;
    name: string;
    growniverseId: string;
    dob: string;
    email?: string;
}

const CertificateValidation: React.FC = () => {
    const [growniverse, setGrowniverse] = useState("");
    const [email, setEmail] = useState("");
    const [dob, setDob] = useState("");
    const [screen, setScreen] = useState<"entry" | "list">("entry");
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const [captchaInput, setCaptchaInput] = useState("");
    const [invalidCaptcha, setInvalidCaptcha] = useState(false);

    /*
     * ============================================================
     * CERTIFICATE RECORDS
     * ============================================================
     */

    const certificates: Certificate[] = [
    {
        number: "IN-291201",
        title: "Internship Certificate - Sales and Marketing",
        duration: "8 June 2026 – 24 July 2026",
        days: "45 Days",
        status: "Completed",
        name: "Navneet Kumar",
        growniverseId: "G100210",
        dob: "2 May 1999",
        email: "navneetrai050@gmail.com",
    },
    {
        number: "IN-291202",
        title: "Internship Certificate - Sales and Marketing",
        duration: "15 June 2026 – 31 July 2026",
        days: "45 Days",
        status: "Completed",
        name: "Anjali",
        growniverseId: "G1002176",
        dob: "21 February 2007",
        email: "anjalitalwara08@gmail.com",
    },
    {
        number: "IN-291203",
        title: "Internship Certificate - Sales and Marketing",
        duration: "15 June 2026 – 31 July 2026",
        days: "45 Days",
        status: "Completed",
        name: "Simran",
        growniverseId: "G1002178",
        dob: "23 February 2007",
        email: "ritubhangal93@gmail.com",
    },
];

    /*
     * ============================================================
     * PAGE STYLES
     * ============================================================
     */

    useEffect(() => {
        loadCaptchaEnginge(6);

        const styles = `
            * {
                box-sizing: border-box;
            }

            .certificate-page {
                min-height: calc(100vh - 80px);
            }

            .certificate-input::placeholder {
                color: #7b8794;
            }

            .certificate-input:focus {
                outline: none !important;
                border-color: #16a34a !important;
                box-shadow:
                    0 0 0 3px rgba(22, 163, 74, 0.08) !important;
            }

            .verify-button:hover {
                transform: translateY(-1px);
                background:
                    linear-gradient(
                        135deg,
                        #15803d,
                        #16a34a
                    ) !important;
                box-shadow:
                    0 10px 25px rgba(22, 163, 74, 0.22) !important;
            }

            .back-button:hover {
                border-color: #16a34a !important;
                color: #15803d !important;
                background: #f0fdf4 !important;
            }

            .certificate-result {
                animation: certificateResultIn 0.6s ease-out;
            }

            .verification-seal {
                animation: sealAppear 0.7s ease-out;
            }

            .certificate-result-card {
                transition: all 0.25s ease;
            }

            .certificate-result-card:hover {
                transform: translateY(-2px);
                box-shadow:
                    0 20px 50px rgba(15, 23, 42, 0.10),
                    0 0 0 1px rgba(22, 163, 74, 0.12);
            }

            .verified-status {
                animation: verifiedPulse 2.5s ease-in-out infinite;
            }

            @keyframes certificateResultIn {
                from {
                    opacity: 0;
                    transform: translateY(25px);
                }

                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            @keyframes sealAppear {
                from {
                    opacity: 0;
                    transform: scale(0.75);
                }

                to {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            @keyframes verifiedPulse {
                0%, 100% {
                    box-shadow:
                        0 0 0 0 rgba(22, 163, 74, 0.12);
                }

                50% {
                    box-shadow:
                        0 0 0 7px rgba(22, 163, 74, 0.03);
                }
            }

            @media (max-width: 768px) {
                .certificate-page {
                    padding: 35px 15px !important;
                }

                .certificate-entry-card,
                .certificate-result {
                    padding: 25px !important;
                }

                .certificate-details-grid {
                    grid-template-columns: 1fr !important;
                }

                .certificate-heading {
                    font-size: 28px !important;
                }

                .certificate-top-section {
                    flex-direction: column !important;
                    text-align: center !important;
                }

                .certificate-verification-info {
                    align-items: center !important;
                }
            }
        `;

        const styleSheet = document.createElement("style");
        styleSheet.innerText = styles;
        document.head.appendChild(styleSheet);

        return () => {
            document.head.removeChild(styleSheet);
        };
    }, []);

    /*
     * ============================================================
     * VERIFY CERTIFICATE
     * ============================================================
     */

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        setInvalidCaptcha(false);
        setIsValid(null);

        /*
         * CAPTCHA VALIDATION
         */
        if (!validateCaptcha(captchaInput)) {
            setInvalidCaptcha(true);
            return;
        }

        /*
         * FORMAT DOB
         *
         * Example:
         * 2007-02-21
         * becomes
         * 21 February 2007
         */
        const formattedDob = dob
            ? new Date(`${dob}T00:00:00`).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
              })
            : "";

        /*
         * ========================================================
         * FIND SPECIFIC CERTIFICATE
         * ========================================================
         *
         * For Navneet:
         * ID + Email + DOB
         *
         * For Anjali / Simran:
         * ID + DOB
         *
         * This makes sure that only the matching certificate
         * is returned.
         */

        const enteredId = growniverse.trim();
        const enteredEmail = email.trim().toLowerCase();

        const matchedCertificate = certificates.find((cert) => {
            /*
             * ID must always match
             */
            if (cert.growniverseId !== enteredId) {
                return false;
            }

            /*
             * DOB must always match
             */
            if (cert.dob !== formattedDob) {
                return false;
            }

            /*
             * If this certificate has an email registered,
             * email must also match.
             *
             * Anjali and Simran currently don't have email
             * records provided, so their ID + DOB is sufficient.
             */
            if (cert.email) {
                return cert.email.toLowerCase() === enteredEmail;
            }

            return true;
        });

        if (matchedCertificate) {
            /*
             * Only return the matching certificate
             */
            setIsValid(true);
            setScreen("list");
        } else {
            setIsValid(false);
        }
    };

    /*
     * ============================================================
     * BACK TO VERIFICATION
     * ============================================================
     */

    const handleBack = () => {
        setScreen("entry");
        setIsValid(null);
        setCaptchaInput("");
        setInvalidCaptcha(false);
        setGrowniverse("");
        setEmail("");
        setDob("");

        setTimeout(() => {
            loadCaptchaEnginge(6);
        }, 100);
    };

    /*
     * ============================================================
     * RENDER
     * ============================================================
     */

    return (
        <>
            <Header />

            {/* =========================================================
                OUTER GROWNIVERSE BACKGROUND
            ========================================================= */}

            <section
                className="certificate-page"
                style={{
                    position: "relative",
                    overflow: "hidden",
                    padding: "65px 20px",
                    background:
                        "linear-gradient(135deg, #050816 0%, #07111f 45%, #0a1024 100%)",
                    color: "#ffffff",
                }}
            >
                {/* =====================================================
                    FUTURISTIC GRID
                ===================================================== */}

                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        opacity: 0.35,
                        backgroundImage:
                            "linear-gradient(rgba(0,246,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(0,246,255,0.035) 1px, transparent 1px)",
                        backgroundSize: "42px 42px",
                    }}
                />

                {/* =====================================================
                    CYAN GLOW
                ===================================================== */}

                <div
                    style={{
                        position: "absolute",
                        width: "500px",
                        height: "500px",
                        left: "-280px",
                        top: "-260px",
                        borderRadius: "50%",
                        background: "rgba(0,246,255,0.09)",
                        filter: "blur(120px)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    PURPLE GLOW
                ===================================================== */}

                <div
                    style={{
                        position: "absolute",
                        width: "500px",
                        height: "500px",
                        right: "-280px",
                        bottom: "-250px",
                        borderRadius: "50%",
                        background: "rgba(139,92,246,0.10)",
                        filter: "blur(120px)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    CENTER BLUE GLOW
                ===================================================== */}

                <div
                    style={{
                        position: "absolute",
                        width: "350px",
                        height: "350px",
                        left: "50%",
                        top: "35%",
                        transform: "translate(-50%, -50%)",
                        borderRadius: "50%",
                        background: "rgba(14,165,233,0.035)",
                        filter: "blur(100px)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    TOP SCAN LINE
                ===================================================== */}

                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "1px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(0,246,255,0.65), rgba(139,92,246,0.5), transparent)",
                        boxShadow:
                            "0 0 15px rgba(0,246,255,0.25)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    BOTTOM LINE
                ===================================================== */}

                <div
                    style={{
                        position: "absolute",
                        bottom: 0,
                        left: "15%",
                        right: "15%",
                        height: "1px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(0,246,255,0.35), transparent)",
                        pointerEvents: "none",
                    }}
                />

                {/* =====================================================
                    MAIN CONTENT
                ===================================================== */}

                <div
                    style={{
                        position: "relative",
                        zIndex: 2,
                        width: "100%",
                        maxWidth:
                            screen === "list"
                                ? "900px"
                                : "650px",
                        margin: "0 auto",
                    }}
                >
                    {/* =================================================
                        ENTRY PAGE
                    ================================================= */}

                    {screen === "entry" && (
                        <div
                            className="certificate-entry-card"
                            style={{
                                background: "#ffffff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "16px",
                                padding: "42px",
                                boxShadow:
                                    "0 25px 70px rgba(0,0,0,0.35), 0 0 40px rgba(0,246,255,0.04)",
                            }}
                        >
                            {/* Official header */}

                            <div
                                style={{
                                    textAlign: "center",
                                    marginBottom: "35px",
                                }}
                            >
                                <div
                                    style={{
                                        width: "58px",
                                        height: "58px",
                                        margin: "0 auto 18px",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#f0fdf4",
                                        border: "1px solid #bbf7d0",
                                        color: "#15803d",
                                        fontSize: "25px",
                                    }}
                                >
                                    ✓
                                </div>

                                <div
                                    style={{
                                        color: "#15803d",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        letterSpacing: "1.8px",
                                        textTransform: "uppercase",
                                        marginBottom: "10px",
                                    }}
                                >
                                    Growniverse Official Portal
                                </div>

                                <h1
                                    className="certificate-heading"
                                    style={{
                                        margin: 0,
                                        fontSize: "36px",
                                        fontWeight: 800,
                                        color: "#0f172a",
                                        letterSpacing: "-0.5px",
                                    }}
                                >
                                    Certificate Verification
                                </h1>

                                <p
                                    style={{
                                        maxWidth: "500px",
                                        margin: "14px auto 0",
                                        color: "#64748b",
                                        fontSize: "14px",
                                        lineHeight: 1.7,
                                    }}
                                >
                                    Verify the authenticity and
                                    validity of a certificate
                                    issued by Growniverse.
                                </p>
                            </div>

                            {/* Official notice */}

                            <div
                                style={{
                                    display: "flex",
                                    gap: "12px",
                                    padding: "15px",
                                    marginBottom: "28px",
                                    borderRadius: "10px",
                                    background: "#f8fafc",
                                    border: "1px solid #e2e8f0",
                                }}
                            >
                                <div
                                    style={{
                                        color: "#15803d",
                                        fontSize: "18px",
                                    }}
                                >
                                    🔐
                                </div>

                                <div>
                                    <div
                                        style={{
                                            color: "#334155",
                                            fontSize: "12px",
                                            fontWeight: 700,
                                            marginBottom: "4px",
                                        }}
                                    >
                                        Secure Verification
                                    </div>

                                    <div
                                        style={{
                                            color: "#64748b",
                                            fontSize: "11px",
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        Please enter the
                                        registered details
                                        exactly as they appear
                                        in our records.
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit}>
                                {/* Growniverse ID */}

                                <label style={labelStyle}>
                                    Growniverse ID
                                </label>

                                <input
                                    type="text"
                                    value={growniverse}
                                    onChange={(e) =>
                                        setGrowniverse(
                                            e.target.value
                                        )
                                    }
                                    required
                                    className="certificate-input"
                                    placeholder="Enter your Growniverse ID"
                                    style={inputStyle}
                                />

                                {/* Email */}

                                <label style={labelStyle}>
                                    Registered Email
                                </label>

                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) =>
                                        setEmail(
                                            e.target.value
                                        )
                                    }
                                    required={
                                        growniverse === "G100210"
                                    }
                                    className="certificate-input"
                                    placeholder="Enter your registered email"
                                    style={inputStyle}
                                />

                                {/* DOB */}

                                <label style={labelStyle}>
                                    Date of Birth
                                </label>

                                <input
                                    type="date"
                                    value={dob}
                                    onChange={(e) =>
                                        setDob(
                                            e.target.value
                                        )
                                    }
                                    required
                                    className="certificate-input"
                                    style={inputStyle}
                                />

                                {/* CAPTCHA */}

                                <div
                                    style={{
                                        marginTop: "5px",
                                        marginBottom: "25px",
                                        padding: "18px",
                                        borderRadius: "12px",
                                        background: "#f8fafc",
                                        border: "1px solid #e2e8f0",
                                    }}
                                >
                                    <div
                                        style={{
                                            marginBottom: "12px",
                                            color: "#475569",
                                            fontSize: "10px",
                                            fontWeight: 800,
                                            letterSpacing: "1px",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Security Verification
                                    </div>

                                    <div
                                        style={{
                                            padding: "12px",
                                            marginBottom: "12px",
                                            background: "#ffffff",
                                            borderRadius: "8px",
                                            border: "1px solid #e2e8f0",
                                            display: "flex",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <LoadCanvasTemplateNoReload />
                                    </div>

                                    <input
                                        type="text"
                                        placeholder="Enter the characters shown above"
                                        value={captchaInput}
                                        onChange={(e) => {
                                            setCaptchaInput(
                                                e.target.value
                                            );

                                            setInvalidCaptcha(
                                                false
                                            );
                                        }}
                                        required
                                        className="certificate-input"
                                        style={{
                                            ...inputStyle,
                                            marginBottom: 0,
                                            background: "#ffffff",
                                        }}
                                    />

                                    {invalidCaptcha && (
                                        <div
                                            style={{
                                                marginTop: "8px",
                                                color: "#dc2626",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                            }}
                                        >
                                            ✕ Invalid security
                                            code. Please try
                                            again.
                                        </div>
                                    )}
                                </div>

                                {/* Verify */}

                                <button
                                    type="submit"
                                    className="verify-button"
                                    style={verifyButtonStyle}
                                >
                                    <span>
                                        VERIFY CERTIFICATE
                                    </span>

                                    <span
                                        style={{
                                            fontSize: "18px",
                                        }}
                                    >
                                        →
                                    </span>
                                </button>
                            </form>

                            {/* Invalid */}

                            {isValid === false && (
                                <div
                                    style={{
                                        marginTop: "20px",
                                        padding: "17px",
                                        borderRadius: "10px",
                                        background: "#fef2f2",
                                        border: "1px solid #fecaca",
                                        textAlign: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#dc2626",
                                            fontSize: "22px",
                                            marginBottom: "5px",
                                        }}
                                    >
                                        ✕
                                    </div>

                                    <strong
                                        style={{
                                            display: "block",
                                            color: "#991b1b",
                                            fontSize: "13px",
                                        }}
                                    >
                                        Certificate Not Found
                                    </strong>

                                    <p
                                        style={{
                                            margin: "5px 0 0",
                                            color: "#b91c1c",
                                            fontSize: "11px",
                                        }}
                                    >
                                        The information
                                        provided could not
                                        be verified against
                                        our records.
                                    </p>
                                </div>
                            )}

                            {/* Footer */}

                            <div
                                style={{
                                    marginTop: "25px",
                                    paddingTop: "18px",
                                    borderTop:
                                        "1px solid #e2e8f0",
                                    textAlign: "center",
                                    color: "#94a3b8",
                                    fontSize: "10px",
                                    lineHeight: 1.6,
                                }}
                            >
                                This verification service is
                                provided by Growniverse for
                                certificate authenticity
                                checking.
                            </div>
                        </div>
                    )}

                    {/* =================================================
                        VERIFIED CERTIFICATE PAGE
                    ================================================= */}

                    {screen === "list" && (
                        <div
                            className="certificate-result"
                            style={{
                                background: "#ffffff",
                                borderRadius: "16px",
                                border: "1px solid #dbe4e8",
                                boxShadow:
                                    "0 30px 80px rgba(0,0,0,0.38), 0 0 45px rgba(0,246,255,0.05)",
                                overflow: "hidden",
                            }}
                        >
                            {/* =================================================
                                OFFICIAL GREEN HEADER
                            ================================================= */}

                            <div
                                style={{
                                    background:
                                        "linear-gradient(135deg, #166534, #15803d)",
                                    padding: "32px 38px",
                                    color: "#ffffff",
                                }}
                            >
                                <div
                                    className="certificate-top-section"
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent:
                                            "space-between",
                                        gap: "25px",
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                fontSize: "10px",
                                                fontWeight: 800,
                                                letterSpacing: "2px",
                                                textTransform:
                                                    "uppercase",
                                                opacity: 0.8,
                                                marginBottom: "8px",
                                            }}
                                        >
                                            Growniverse
                                        </div>

                                        <h1
                                            style={{
                                                margin: 0,
                                                fontSize:
                                                    "clamp(25px, 4vw, 34px)",
                                                fontWeight: 800,
                                            }}
                                        >
                                            Certificate Verification
                                        </h1>

                                        <p
                                            style={{
                                                margin: "8px 0 0",
                                                fontSize: "12px",
                                                opacity: 0.85,
                                            }}
                                        >
                                            Official certificate
                                            authenticity record
                                        </p>
                                    </div>

                                    {/* Verification Seal */}

                                    <div
                                        className="verification-seal"
                                        style={{
                                            width: "100px",
                                            height: "100px",
                                            minWidth: "100px",
                                            borderRadius: "50%",
                                            border:
                                                "2px solid rgba(255,255,255,0.65)",
                                            display: "flex",
                                            flexDirection:
                                                "column",
                                            alignItems: "center",
                                            justifyContent:
                                                "center",
                                            background:
                                                "rgba(255,255,255,0.10)",
                                            boxShadow:
                                                "0 8px 25px rgba(0,0,0,0.12)",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: "30px",
                                                lineHeight: 1,
                                                marginBottom: "3px",
                                            }}
                                        >
                                            ✓
                                        </div>

                                        <div
                                            style={{
                                                fontSize: "8px",
                                                fontWeight: 800,
                                                letterSpacing: "1px",
                                            }}
                                        >
                                            VERIFIED
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* =================================================
                                VERIFIED STATUS
                            ================================================= */}

                            <div
                                className="verified-status"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "9px",
                                    padding: "13px",
                                    background: "#f0fdf4",
                                    borderBottom:
                                        "1px solid #dcfce7",
                                    color: "#166534",
                                    fontSize: "11px",
                                    fontWeight: 800,
                                    letterSpacing: "0.8px",
                                    textTransform: "uppercase",
                                }}
                            >
                                <span
                                    style={{
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        background: "#16a34a",
                                    }}
                                />

                                Certificate Successfully Verified
                            </div>

                            {/* =================================================
                                RESULT CONTENT
                            ================================================= */}

                            <div
                                style={{
                                    padding: "38px",
                                }}
                            >
                                {/* Heading */}

                                <div
                                    style={{
                                        textAlign: "center",
                                        marginBottom: "32px",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#64748b",
                                            fontSize: "10px",
                                            fontWeight: 800,
                                            letterSpacing: "1.5px",
                                            textTransform:
                                                "uppercase",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        Verified Credential
                                    </div>

                                    <h2
                                        className="certificate-heading"
                                        style={{
                                            margin: 0,
                                            color: "#0f172a",
                                            fontSize:
                                                "clamp(24px, 4vw, 32px)",
                                            fontWeight: 800,
                                        }}
                                    >
                                        Certificate Found
                                    </h2>

                                    <p
                                        style={{
                                            maxWidth: "600px",
                                            margin: "10px auto 0",
                                            color: "#64748b",
                                            fontSize: "13px",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        The following certificate
                                        has been successfully
                                        verified against
                                        Growniverse records.
                                    </p>
                                </div>

                                {/* =================================================
                                    ONLY MATCHED CERTIFICATE
                                ================================================= */}

                                {certificates
                                    .filter((cert) => {
                                        const formattedDob = dob
                                            ? new Date(
                                                  `${dob}T00:00:00`
                                              ).toLocaleDateString(
                                                  "en-GB",
                                                  {
                                                      day: "numeric",
                                                      month: "long",
                                                      year: "numeric",
                                                  }
                                              )
                                            : "";

                                        if (
                                            cert.growniverseId !==
                                            growniverse.trim()
                                        ) {
                                            return false;
                                        }

                                        if (
                                            cert.dob !==
                                            formattedDob
                                        ) {
                                            return false;
                                        }

                                        if (cert.email) {
                                            return (
                                                cert.email.toLowerCase() ===
                                                email
                                                    .trim()
                                                    .toLowerCase()
                                            );
                                        }

                                        return true;
                                    })
                                    .map((cert) => (
                                        <div
                                            key={cert.number}
                                            className="certificate-result-card"
                                            style={{
                                                position:
                                                    "relative",
                                                overflow:
                                                    "hidden",
                                                border:
                                                    "1px solid #dbe4e8",
                                                borderRadius:
                                                    "14px",
                                                background:
                                                    "#ffffff",
                                                boxShadow:
                                                    "0 12px 35px rgba(15,23,42,0.06)",
                                            }}
                                        >
                                            {/* Green top */}

                                            <div
                                                style={{
                                                    height: "5px",
                                                    background:
                                                        "linear-gradient(90deg, #166534, #22c55e, #166534)",
                                                }}
                                            />

                                            <div
                                                style={{
                                                    padding: "30px",
                                                }}
                                            >
                                                {/* Certificate title */}

                                                <div
                                                    style={{
                                                        display:
                                                            "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        alignItems:
                                                            "flex-start",
                                                        gap: "20px",
                                                        marginBottom:
                                                            "28px",
                                                    }}
                                                >
                                                    <div>
                                                        <div
                                                            style={{
                                                                color: "#15803d",
                                                                fontSize:
                                                                    "10px",
                                                                fontWeight:
                                                                    800,
                                                                letterSpacing:
                                                                    "1.3px",
                                                                textTransform:
                                                                    "uppercase",
                                                                marginBottom:
                                                                    "8px",
                                                            }}
                                                        >
                                                            Official
                                                            Certificate
                                                        </div>

                                                        <h3
                                                            style={{
                                                                margin: 0,
                                                                color: "#0f172a",
                                                                fontSize:
                                                                    "20px",
                                                                lineHeight:
                                                                    1.45,
                                                                fontWeight:
                                                                    750,
                                                            }}
                                                        >
                                                            {
                                                                cert.title
                                                            }
                                                        </h3>
                                                    </div>

                                                    <div
                                                        style={{
                                                            flexShrink: 0,
                                                            padding:
                                                                "7px 12px",
                                                            borderRadius:
                                                                "20px",
                                                            background:
                                                                "#f0fdf4",
                                                            border:
                                                                "1px solid #bbf7d0",
                                                            color: "#15803d",
                                                            fontSize:
                                                                "9px",
                                                            fontWeight:
                                                                800,
                                                            letterSpacing:
                                                                "0.8px",
                                                        }}
                                                    >
                                                        ✓ VALID
                                                    </div>
                                                </div>

                                                {/* Details */}

                                                <div
                                                    className="certificate-details-grid"
                                                    style={{
                                                        display:
                                                            "grid",
                                                        gridTemplateColumns:
                                                            "repeat(2, minmax(0, 1fr))",
                                                        gap: "12px",
                                                    }}
                                                >
                                                    <CertificateDetail
                                                        label="Certificate Holder"
                                                        value={
                                                            cert.name
                                                        }
                                                    />

                                                    <CertificateDetail
                                                        label="Certificate Number"
                                                        value={
                                                            cert.number
                                                        }
                                                    />

                                                    <CertificateDetail
                                                        label="Growniverse ID"
                                                        value={
                                                            cert.growniverseId
                                                        }
                                                    />

                                                    <CertificateDetail
                                                        label="Program Duration"
                                                        value={
                                                            cert.duration
                                                        }
                                                    />

                                                    <CertificateDetail
                                                        label="Internship Duration"
                                                        value={
                                                            cert.days
                                                        }
                                                    />

                                                    <CertificateDetail
                                                        label="Certificate Status"
                                                        value={
                                                            cert.status
                                                        }
                                                    />
                                                </div>

                                                {/* =================================================
                                                    VERIFICATION INFORMATION
                                                ================================================= */}

                                                <div
                                                    style={{
                                                        marginTop:
                                                            "18px",
                                                        padding:
                                                            "17px",
                                                        borderRadius:
                                                            "10px",
                                                        background:
                                                            "#f8fafc",
                                                        border:
                                                            "1px solid #e2e8f0",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display:
                                                                "flex",
                                                            gap: "10px",
                                                            alignItems:
                                                                "flex-start",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: "28px",
                                                                height: "28px",
                                                                minWidth:
                                                                    "28px",
                                                                borderRadius:
                                                                    "50%",
                                                                background:
                                                                    "#dcfce7",
                                                                color: "#15803d",
                                                                display:
                                                                    "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                fontSize:
                                                                    "14px",
                                                                fontWeight:
                                                                    800,
                                                            }}
                                                        >
                                                            ✓
                                                        </div>

                                                        <div>
                                                            <div
                                                                style={{
                                                                    color: "#334155",
                                                                    fontSize:
                                                                        "12px",
                                                                    fontWeight:
                                                                        750,
                                                                    marginBottom:
                                                                        "4px",
                                                                }}
                                                            >
                                                                Verification
                                                                Confirmed
                                                            </div>

                                                            <div
                                                                style={{
                                                                    color: "#64748b",
                                                                    fontSize:
                                                                        "10px",
                                                                    lineHeight:
                                                                        1.6,
                                                                }}
                                                            >
                                                                This certificate
                                                                record has been
                                                                successfully
                                                                matched with the
                                                                information
                                                                provided to the
                                                                Growniverse
                                                                verification
                                                                system.
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* =================================================
                                                    OFFICIAL FOOTER
                                                ================================================= */}

                                                <div
                                                    style={{
                                                        display:
                                                            "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        alignItems:
                                                            "center",
                                                        gap: "15px",
                                                        marginTop:
                                                            "22px",
                                                        paddingTop:
                                                            "18px",
                                                        borderTop:
                                                            "1px solid #e2e8f0",
                                                    }}
                                                >
                                                    <div>
                                                        <div
                                                            style={{
                                                                color: "#94a3b8",
                                                                fontSize:
                                                                    "8px",
                                                                fontWeight:
                                                                    800,
                                                                letterSpacing:
                                                                    "1px",
                                                                textTransform:
                                                                    "uppercase",
                                                                marginBottom:
                                                                    "4px",
                                                            }}
                                                        >
                                                            Issuing
                                                            Organization
                                                        </div>

                                                        <div
                                                            style={{
                                                                color: "#334155",
                                                                fontSize:
                                                                    "11px",
                                                                fontWeight:
                                                                    700,
                                                            }}
                                                        >
                                                            Growniverse
                                                        </div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            textAlign:
                                                                "right",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                color: "#94a3b8",
                                                                fontSize:
                                                                    "8px",
                                                                fontWeight:
                                                                    800,
                                                                letterSpacing:
                                                                    "1px",
                                                                textTransform:
                                                                    "uppercase",
                                                                marginBottom:
                                                                    "4px",
                                                            }}
                                                        >
                                                            Verification
                                                            Status
                                                        </div>

                                                        <div
                                                            style={{
                                                                color: "#15803d",
                                                                fontSize:
                                                                    "11px",
                                                                fontWeight:
                                                                    800,
                                                            }}
                                                        >
                                                            ✓ AUTHENTIC
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                {/* =================================================
                                    BACK BUTTON
                                ================================================= */}

                                <button
                                    onClick={handleBack}
                                    className="back-button"
                                    style={{
                                        width: "100%",
                                        marginTop: "25px",
                                        padding: "13px",
                                        border:
                                            "1px solid #cbd5e1",
                                        borderRadius: "9px",
                                        background: "#ffffff",
                                        color: "#475569",
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        fontWeight: 800,
                                        letterSpacing: "0.8px",
                                        transition:
                                            "all 0.2s ease",
                                    }}
                                >
                                    ← VERIFY ANOTHER CERTIFICATE
                                </button>

                                {/* Disclaimer */}

                                <div
                                    style={{
                                        textAlign: "center",
                                        marginTop: "20px",
                                        color: "#94a3b8",
                                        fontSize: "9px",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    This page confirms the
                                    certificate information
                                    available in the Growniverse
                                    verification system.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

/* ============================================================
   CERTIFICATE DETAIL COMPONENT
============================================================ */

interface CertificateDetailProps {
    label: string;
    value: string;
}

const CertificateDetail: React.FC<
    CertificateDetailProps
> = ({ label, value }) => {
    return (
        <div
            style={{
                padding: "16px",
                borderRadius: "9px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
            }}
        >
            <div
                style={{
                    color: "#94a3b8",
                    fontSize: "8px",
                    fontWeight: 800,
                    letterSpacing: "1.1px",
                    textTransform: "uppercase",
                    marginBottom: "7px",
                }}
            >
                {label}
            </div>

            <div
                style={{
                    color: "#1e293b",
                    fontSize: "12px",
                    fontWeight: 650,
                    lineHeight: 1.5,
                }}
            >
                {value}
            </div>
        </div>
    );
};

/* ============================================================
   FORM LABEL
============================================================ */

const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: "7px",
    color: "#334155",
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
};

/* ============================================================
   INPUT
============================================================ */

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "13px 14px",
    marginBottom: "19px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: "13px",
    transition: "all 0.2s ease",
};

/* ============================================================
   VERIFY BUTTON
============================================================ */

const verifyButtonStyle: React.CSSProperties = {
    width: "100%",
    padding: "14px 20px",
    border: "none",
    borderRadius: "8px",
    background:
        "linear-gradient(135deg, #166534, #15803d)",
    color: "#ffffff",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "1px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    transition: "all 0.2s ease",
    boxShadow:
        "0 6px 18px rgba(22,163,74,0.15)",
};

export default CertificateValidation;